import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Container,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  VerifiedUser as CertificateIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const HomeHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const navigationItems = [
    {
      title: 'Home',
      path: '/main/',
      href: '#home',
    },
    {
      title: 'About',
      path: '/main/',
      href: '#about',
    },
    {
      title: 'Accreditation',
      path: '/main/',
      href: '#accreditation',
    },
    {
      title: 'Register',
      path: '/main/register',
      href: null,
    },
    {
      title: 'Login',
      path: '/main/login',
      href: null,
    },
    {
      title: 'Contact',
      path: '/main/',
      href: '#contact',
    },
  ];

  const handleNavClick = (item) => {
    if (item.href && item.href.startsWith('#')) {
      // Handle scroll to section
      if (location.pathname !== '/main/') {
        navigate('/main/');
        // Wait for navigation then scroll
        setTimeout(() => {
          const element = document.querySelector(item.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.querySelector(item.href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      // Handle route navigation
      navigate(item.path);
    }
  };

  const isActiveItem = (item) => {
    if (item.href && item.href.startsWith('#')) {
      return location.pathname === '/main/' && location.hash === item.href;
    }
    return location.pathname === item.path;
  };

  const renderDesktopNav = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {navigationItems.map((item, index) => (
        <Button
          key={index}
          onClick={() => handleNavClick(item)}
          sx={{
            color: 'white',
            fontWeight: 500,
            textTransform: 'none',
            fontSize: '1rem',
            px: 3,
            py: 1,
            mx: 0.5,
            borderRadius: 0,
            position: 'relative',
            backgroundColor: isActiveItem(item) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              width: isActiveItem(item) ? '80%' : '0%',
              height: '2px',
              backgroundColor: 'white',
              transform: 'translateX(-50%)',
              transition: 'width 0.3s ease',
            },
            '&:hover::after': {
              width: '80%',
            },
          }}
        >
          {item.title}
        </Button>
      ))}
    </Box>
  );

  const renderMobileNav = () => (
    <>
      <IconButton
        color="inherit"
        onClick={toggleMobileDrawer}
        sx={{ 
          ml: 'auto',
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
        }}
      >
        <MenuIcon />
      </IconButton>
      <Drawer
        anchor="right"
        open={mobileDrawerOpen}
        onClose={toggleMobileDrawer}
        PaperProps={{
          sx: { 
            width: 280, 
            background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
          },
        }}
      >
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CertificateIcon sx={{ color: 'white', mr: 1, fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
              NABL
            </Typography>
          </Box>
          <IconButton onClick={toggleMobileDrawer} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        
        <List sx={{ px: 1, py: 2 }}>
          {navigationItems.map((item, index) => (
            <ListItem
              key={index}
              button
              onClick={() => {
                handleNavClick(item);
                toggleMobileDrawer();
              }}
              sx={{
                borderRadius: 1,
                mb: 1,
                backgroundColor: isActiveItem(item) ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ListItemText 
                primary={item.title}
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    color: 'white',
                    fontWeight: isActiveItem(item) ? 600 : 500,
                    fontSize: '1rem',
                  } 
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>
    </>
  );

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        background: 'linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)',
        boxShadow: '0 2px 16px rgba(0, 2, 76, 0.2)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      }}
    >
      <Container maxWidth="lg">
        <Toolbar sx={{ minHeight: 72, px: 0 }}>
          {/* Brand/Logo Section */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              '&:hover': {
                opacity: 0.9,
              },
              transition: 'opacity 0.3s ease',
            }}
            
          >
            <CertificateIcon 
              sx={{ 
                color: 'white', 
                mr: 1.5, 
                fontSize: { xs: 28, md: 32 },
              }} 
            />
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                color: 'white',
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                letterSpacing: '1px',
              }}
            >
              NABL
            </Typography>
          </Box>

          {/* Navigation */}
          <Box sx={{ flexGrow: 1 }} />
          {isMobile ? renderMobileNav() : renderDesktopNav()}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default HomeHeader;
